import { Form, Typography, Row, Col, Button, Spin } from "antd";
import _ from 'lodash';
import { MODAL_SIZE_MEDIUM, BASE_GUTTER } from 'constants/Layout'
import BaseModal from "components/BaseModal";
import React, { forwardRef, useEffect, useRef, useCallback, useMemo, useState } from "react";
import FindingAPI from "restAPI/FindingAPI";
import { checkCharLimit, generateFormFields } from "utils/formUtils";
import { getDetailedInfoFields } from './ManualFindingPopup';
import message from 'components/CustomMessage';
import htmlToMarkDown, { sanitizeMarkdown } from "utils/htmlToMarkDownUtil";
import { marked } from "marked";
import { MANUAL_FINDING_MARK_DOWN_FIELDS } from "./Constants";
import AppLoader from 'components/AppLoader';
import InputWithPlaceholder from 'components/InputWithPlaceholder'
import { convertCommentToNewFormat } from "./FindingDetails/common";

const ManualFindingTemplateForm = forwardRef(({ templates, templateData, afterClose = _.noop }, ref) => {
    const [loading, setLoading] = useState(false)
    const [findingId, setFindingId] = useState();
    const [errorText, setErrorText] = useState();
    const [form] = Form.useForm();
    const isEditMode = !_.isUndefined(templateData);
    const templateRef = useRef();
    const editorRef = useRef({});

    useEffect(() => {
        if (isEditMode) {
            const { name, template } = templateData;

            // convert markdown to html
            MANUAL_FINDING_MARK_DOWN_FIELDS.forEach(field => {
                if (template[field]) {
                    template[field] = convertCommentToNewFormat(marked.parse(template[field]));
                    if (editorRef.current[field].setValue) {
                        editorRef.current[field].setValue(template[field])
                    }
                }
            });

            const fieldData = { name, ...template };
            Object.keys(fieldData).forEach(field => {
                form.setFieldsValue({ [field]: fieldData[field] });
            })
        }
    }, [isEditMode, form, templateData])

    const fields = useMemo(() => [
        {
            name: 'name',
            label: "Name",
            required:true,
            rules: [
                checkCharLimit(50),
                {
                    validator: (record, value) => {
                        if (value) {
                            value = value.trim();
                            const list = _.map(templates, (templateData, templateName) => templateName.toLowerCase());
                            if (value && list.includes(value.toLowerCase()) && value !== templateData?.name) {
                                return Promise.reject("Template name already exists")
                            }
                        }
                        return Promise.resolve()
                    }
                }
            ],
            colSpan: 24
        },
        ...getDetailedInfoFields(form, editorRef, ["vendor", "scanDate"])
    ], [templates, templateData, form]);

    const modalProps = {
        title: `${isEditMode ? 'Update' : 'Create'} Manual Finding Template`,
        okText: "Save",
        className: "modal-full-height no-footer", 
        width: MODAL_SIZE_MEDIUM
    }

    const configureTemplate = useCallback(async () => {
        await form.validateFields();
        const values = form.getFieldsValue();
        const { name, cvssCalculator, ...template } = values;
        
        Object.keys(template).forEach(key => {
            if (_.isUndefined(template[key])) {
                template[key] = null;
            }
        });
        
        // convert html to markdown
        MANUAL_FINDING_MARK_DOWN_FIELDS.forEach(field => {
            if (template[field]) {
                template[field] = sanitizeMarkdown(htmlToMarkDown(template[field]));
            }
        });

        const methodName = isEditMode ? 'updateManualFindingTemplates' : 'addManualFindingTemplates';
        const data = { name, template };
        if (isEditMode) {
            data.oldName = templateData.name;
        }
        const { config }  = await FindingAPI[methodName](data);
        message.success(`Successfully ${isEditMode ? 'Updated': 'Added new'} Finding Template`);
        templateRef.current = config;
    }, [isEditMode, templateData, form]);

    const _afterClose = () => {
        afterClose(templateRef.current)
    }
    const handlePopulateClick = useCallback(async () => {
        setLoading(true)
        setErrorText();
        try {
            const data = await FindingAPI.get(findingId).promise;
            const fieldValues = form.getFieldsValue();
            const OTHER_FIELDS = ["title", "severity", "scanType", "scanDate", "testType", "armorcodeCategory", "cwe", "cve", "vendor", "cvssVector", "cvssScore"];
            [...MANUAL_FINDING_MARK_DOWN_FIELDS, ...OTHER_FIELDS].forEach(key => {
                let value = _.get(data, key);
                if (value && MANUAL_FINDING_MARK_DOWN_FIELDS.includes(key)) {
                    value = convertCommentToNewFormat(marked.parse(value));
                    if (editorRef.current[key].setValue) {
                        editorRef.current[key].setValue(value)
                    }
                } else if (["cwes", "cves"].includes(`${key}s`)) {
                    value = _.get(data, `${key}s`);
                    if (_.isArray(value)) {
                        value = value[0];
                    }
                } else if (key === "testType") {
                    value = _.get(data, 'findingCategory')
                } else if (key === "cvssScore") {
                    value = _.get(data, 'baseScore')
                } else if (key === 'armorcodeCategory') {
                    value =  _.get(data, 'category');
                }
                if (value) {
                    fieldValues[key] = value;
                }
            })
            form.setFieldsValue({...fieldValues});
        } catch (e) {
            setErrorText("There are no findings with this finding Id.")
        }finally {
            setLoading(false)
        }
    }, [findingId, form])

    return (
        <BaseModal
            modalProps={{
                ...modalProps,
                onOk: configureTemplate
            }}
            ref={ref}
            afterClose={_afterClose}>
                <Spin spinning={loading} indicator={<AppLoader style={{ marginTop: 0 }} />}>
                    <Row className="m-b-ms" gutter={BASE_GUTTER} align="bottom">
                        <Col span={12}>
                            <Typography.Text>Create From Finding</Typography.Text>
                            <InputWithPlaceholder 
                                value={findingId} 
                                onChange={({currentTarget}) => setFindingId(currentTarget.value)}
                                help='Not a valid input'
                            />
                        </Col>
                        <Col span={12}>
                            <Button disabled={!findingId} type="primary" onClick={handlePopulateClick}>Populate</Button>
                        </Col>
                        { errorText && <Col span={12} className="p-t-0">
                            <Typography.Text type="danger">{errorText}</Typography.Text>
                        </Col>}
                    </Row>
                    <Form layout="vertical" form={form}>
                        { generateFormFields(fields)}
                    </Form>
                </Spin>
        </BaseModal>
    )

})

export default ManualFindingTemplateForm;
